import { AttackPattern, PatternState } from '../AttackPattern.js';

/**
 * Ostinato (Act III's opener and the act's defining texture — ACT3-BIBLE §3).
 * An ostinato is a figure that repeats until it stops being a choice; this one
 * repeats *you*. It reads the player's own movement history (Player.pathHistory,
 * the ring buffer this act adds) and, `replayDelay` seconds later, ignites the
 * ground along exactly the path you walked.
 *
 * The near-miss it must not become: the Ashen Throne's Ember Wake ignites where
 * you are standing, immediately, which is a leash on lingering. This arrives on
 * a DELAY, so it argues with the *shape* of your movement instead of its
 * recency — the loop you have drilled for a hundred runs is the thing that
 * kills you, and a path you are willing to never revisit is free.
 *
 * The bible's §11 playtest lever ("is being argued with by your own habits fun,
 * or just annoying?") is built in from the start rather than held back: the
 * whole pending path is drawn as a faint ghost line the moment it is recorded,
 * so the counterplay is legible on screen rather than remembered. `ghostLead`
 * is how far ahead of the burning head that preview reaches.
 *
 * Escalation is `replayDelay` shortening per phase — your own past closing on
 * you — plus Stretto (`strettoPhase`), which replays the same buffer at a
 * second, tighter offset so the arena carries two of you.
 */
export class Ostinato extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Ostinato', telegraphDuration: 0.9, activeDuration: 6.4, minPhase });
    this.config = config;
  }

  onBegin(_boss, player) {
    this._burnTimer = 0;
    this._heads = [];
    // getVisual takes no player argument and the ghost line needs one, so the
    // reference is latched here and refreshed on every tick below.
    this._player = player;
  }

  /** Replay delay for this phase — 3.0 → 2.0 → 1.4 as the fight escalates. */
  _delayFor(boss) {
    const shortenPerPhase = this.config.shortenPerPhase ?? 0.55;
    return Math.max(this.config.minDelay ?? 1.2, this.config.replayDelay - boss.phase * shortenPerPhase);
  }

  /** The offsets (seconds into the past) currently being replayed as hazard. */
  _offsets(boss) {
    const delay = this._delayFor(boss);
    if (boss.phase >= (this.config.strettoPhase ?? 99)) {
      // Stretto: the same figure, entered twice, a half-length apart.
      return [delay, delay * 0.55];
    }
    return [delay];
  }

  onActiveTick(dt, boss, player, _pool) {
    this._player = player;
    this._burnTimer -= dt;
    const width = this.config.trailWidth;
    this._heads = [];
    let touched = false;
    for (const offset of this._offsets(boss)) {
      const head = player.positionAgo(offset);
      if (!head) continue;
      this._heads.push(head);
      if (Math.hypot(player.x - head.x, player.y - head.y) < width + player.radius) touched = true;
    }
    // One tick of damage per burn interval regardless of how many heads are
    // overlapping — Stretto is meant to crowd the floor, not to double the
    // damage rate of standing still.
    if (touched && this._burnTimer <= 0 && !player.isInvincible) {
      player.takeDamage(this.config.tickDamage, this.name);
      this._burnTimer = 0.4;
    }
  }

  getVisual(boss) {
    if (this.state !== PatternState.TELEGRAPH && this.state !== PatternState.ACTIVE) return null;
    const player = this._player;
    return {
      kind: 'ostinatoTrail',
      width: this.config.trailWidth,
      imminent: this.state === PatternState.ACTIVE,
      heads: this._heads ?? [],
      // The legibility lever: the stretch of path between the burning head and
      // (roughly) now, drawn faint, so the player can see what is about to
      // ignite instead of having to remember where they walked.
      ghost: player ? player.pathBetween(this._delayFor(boss), this.config.ghostLead ?? 0.15) : [],
    };
  }

  onTelegraphTick(_dt, _boss, player) {
    this._player = player;
  }
}
